// ===================== //
// === Enums and Types === //
// ===================== //

/**
 * 🎨 Icon Types
 * A `const` object representing the available icon types in the application.
 * Each key is a unique identifier, and its value corresponds to the string used for the icon.
 */
export const LogoTypes = {
  Crowdstrike: 'crowdstrike',
  Elastic: 'elastic',
  Yara: 'yara',
  Snort: 'snort',
  SentinelOne: 'sentinel-one',
  Tanium: 'tanium',
  Splunk: 'splunk',
  TrendMicro: 'trend-micro',
  Corelight: 'corelight',
  Carbonblack: 'carbonblack',
  PaloAltoNetworks: 'palo-alto-networks',
  PaloAltoNetworksCortex: 'palo-alto-networks-cortex',
  MicrosoftSentinel: 'microsoft-sentinel',
  MicrosoftDefender: 'microsoft-defender',
} as const;

/**
 * 🎭 IconKeysType
 * A dynamically derived type representing all the keys of `LogoTypes` (e.g., 'Crowdstrike', 'Elastic').
 */
export type LogoKeysType = keyof typeof LogoTypes;

/**
 * 🎭 IconValuesType
 * A dynamically derived type representing all the string values of `LogoTypes` (e.g., 'crowdstrike', 'elastic').
 */
export type LogoValuesType = (typeof LogoTypes)[LogoKeysType];

// ===================== //
// === Storybook Lists === //
// ===================== //

/**
 * 📚 Storybook Controls
 * Exported lists for Storybook dropdown controls.
 */
export const storybookIconsList: LogoValuesType[] = Object.values(LogoTypes);

// ===================== //
// === Utility Matcher === //
// ===================== //

/**
 * 🔍 getIconForProduct
 * Tries to match a raw tool.product string to a known LogoValuesType.
 * If it can't find a match, returns a fallback icon.
 */
export const getIconForProduct = (product: string): LogoValuesType => {
  if (!product) return LogoTypes.Crowdstrike;

  const normalized = product.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9-]/gi, '');

  const entries = Object.entries(LogoTypes);

  return (
    entries.find(([, id]) =>
      id.toLowerCase().replace(/[^a-z0-9-]/gi, '') === normalized
    )?.[1] ?? LogoTypes.Crowdstrike
  );
};
